/**
 * UNIFIED AUTHENTICATION HOOK
 * 
 * This hook exposes one BotSpot application session. Auth0 performs the hosted
 * identity ceremony, then the backend exchanges that identity for a BotSpot
 * custom JWT before this hook reports an authenticated user.
 * 
 * AUTHENTICATION METHODS:
 * 
 * AUTH0 FLOW:
 * - User clicks login/signup button
 * - Redirected to Auth0 for authentication
 * - Returns with an Auth0 identity token
 * - Backend exchanges the identity for a BotSpot custom JWT
 * - BotSpot JWT is used for all application API requests
 * 
 * CUSTOM JWT FLOW (Post-Purchase):
 * - User completes Stripe payment
 * - Backend generates custom JWT with 15-minute expiry
 * - Frontend receives auto-login URL with JWT
 * - JWT automatically processed and user logged in
 * - No Auth0 interaction required
 * - Immediate access to protected routes and API
 * 
 * API TOKEN MANAGEMENT:
 * - Uses only BotSpot custom JWTs for application API requests
 * - Never exposes an Auth0 access token as an application-session fallback
 * 
 * USER ROLES AND PERMISSIONS:
 * - Admin status determined by backend user profile
 * - Payment status fetched from separate PaymentStatusContext
 * - Role-based access control for admin features
 * 
 * ERROR HANDLING:
 * - Auth0 identity refresh before the BotSpot session exchange
 * - Expired JWT detection and cleanup
 * - Graceful fallback to login prompts
 * - Comprehensive error logging
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useUnifiedAuth } from '../contexts/UnifiedAuthContext';
import { usePaymentStatus } from '../contexts/PaymentStatusContext';
import { safeGetStorageItem } from '../utils/browserStorage';
import fetchWithTimeout from '../utils/fetchWithTimeout';
import getApiBaseUrl from '../utils/apiBaseUrl';
import readJsonResponse from '../utils/readJsonResponse';

const inFlightProfileRequests = new Map();

const getSharedProfileRequest = (profileKey, requestFactory) => {
  const existingRequest = inFlightProfileRequests.get(profileKey);
  if (existingRequest) {
    return existingRequest;
  }

  const request = Promise.resolve().then(requestFactory);
  inFlightProfileRequests.set(profileKey, request);
  const clearRequest = () => {
    if (inFlightProfileRequests.get(profileKey) === request) {
      inFlightProfileRequests.delete(profileKey);
    }
  };
  request.then(clearRequest, clearRequest);
  return request;
};

const useUser = () => {
  // Get unified authentication state
  const {
    user,
    isAuthenticated,
    isLoading: authLoading,
    authMethod,
    authSessionExchangeFailed,
    clearAuthSessionExchangeFailure,
    loginWithRedirect,
    logout,
    handleCustomJwtAuth,
    getApiToken,
    getFreshLoginIdToken
  } = useUnifiedAuth();

  // Get payment status from separate context
  const {
    isPayingCustomer,
    isLoading: paymentLoading,
    error: paymentError,
    requiresSessionRefresh
  } = usePaymentStatus();

  // Local state for user profile data
  const [userProfile, setUserProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isBetaTester, setIsBetaTester] = useState(false);
  const profileRequestSequenceRef = useRef(0);

  // Extract stable user identifiers to prevent re-render cascades
  // Using individual fields instead of entire user object maintains stable dependencies
  const userId = user?.id;
  const userEmail = user?.email;
  const userSub = user?.sub || user?.auth0Sub;

  const buildFallbackProfile = useCallback(() => ({
    email: userEmail || null,
    id: userId || userSub,
    role: 'user',
    hasCompletedOnboarding: false,
    authMethod: authMethod
  }), [userEmail, userId, userSub, authMethod]);

  /**
   * FETCH USER PROFILE FROM BACKEND
   * Gets additional user data including admin status and profile details
   * Dependencies are individual user fields to prevent unnecessary re-fetches
   */
  const fetchUserProfile = useCallback(async ({ forceFresh = false } = {}) => {
    if (!isAuthenticated || (!userId && !userSub)) {
      console.debug('[useUser] Not authenticated, skipping profile fetch');
      return;
    }

    const requestSequence = profileRequestSequenceRef.current + 1;
    profileRequestSequenceRef.current = requestSequence;

    try {
      setProfileLoading(true);
      const profileKey = `${authMethod || 'unknown'}:${userId || userSub || userEmail}`;
      if (forceFresh) {
        inFlightProfileRequests.delete(profileKey);
      }
      const profileData = await getSharedProfileRequest(profileKey, async () => {
        // Backend identifies the user from the token; email is not required.
        console.debug('[useUser] Fetching user profile for user:', {
          email: userEmail || 'undefined',
          id: userId || 'undefined',
          auth0Sub: userSub || 'undefined',
          authMethod: authMethod
        });

        const token = await getApiToken();
        if (!token) {
          console.warn('[useUser] No API token available for profile fetch');
          return buildFallbackProfile();
        }

        const response = await fetchWithTimeout(`${getApiBaseUrl()}/users/user_profile`, {
          method: 'GET',
          cache: 'no-store',
          headers: {
            'Accept': 'application/json',
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok) {
          console.warn('[useUser] Failed to fetch user profile:', response.status);
          return buildFallbackProfile();
        }

        const responseData = await readJsonResponse(response, { errorMessage: 'Failed to fetch user profile' });
        const resolvedProfile = responseData.profile || responseData;
        console.debug('[useUser] User profile fetched successfully:', {
          email: resolvedProfile.email,
          firstName: resolvedProfile.firstName,
          lastName: resolvedProfile.lastName,
          phone: resolvedProfile.phone,
          role: resolvedProfile.role,
          hasCompletedOnboarding: resolvedProfile.hasCompletedOnboarding,
          authMethod: authMethod,
          fullProfileData: resolvedProfile
        });
        return resolvedProfile;
      });

      if (profileRequestSequenceRef.current === requestSequence) {
        setUserProfile(profileData);
        setIsAdmin(profileData.role === 'admin');
        setIsBetaTester(profileData.role === 'beta_tester');
      }
      return profileData;
    } catch (error) {
      if (error?.name === 'AbortError') {
        console.warn('[useUser] User profile fetch timed out; using fallback profile');
      } else {
        console.error('[useUser] Error fetching user profile:', error);
      }

      // Create fallback profile on error
      const fallbackProfile = buildFallbackProfile();
      if (profileRequestSequenceRef.current === requestSequence) {
        setUserProfile(fallbackProfile);
        setIsAdmin(false);
        setIsBetaTester(false);
      }
      return fallbackProfile;
    } finally {
      if (profileRequestSequenceRef.current === requestSequence) {
        setProfileLoading(false);
      }
    }
  }, [isAuthenticated, userId, userEmail, userSub, authMethod, getApiToken, buildFallbackProfile]);

  const refreshProfile = useCallback(
    () => fetchUserProfile({ forceFresh: true }),
    [fetchUserProfile]
  );

  /**
   * FETCH PROFILE WHEN AUTHENTICATION STATE CHANGES
   * Automatically loads user profile when user logs in or auth method changes
   * Uses stable user identifiers to prevent unnecessary re-fetches
   */
  useEffect(() => {
    if (isAuthenticated && (userId || userSub) && !authLoading) {
      console.debug('[useUser] Authentication state changed, fetching profile');
      fetchUserProfile();
    } else if (!isAuthenticated) {
      // Clear profile data when user logs out
      console.debug('[useUser] User logged out, clearing profile data');
      setUserProfile(null);
      setIsAdmin(false);
      setIsBetaTester(false);
    }
  }, [isAuthenticated, userId, userSub, authLoading, fetchUserProfile]);

  /**
   * Compatibility adapter for useSSE and UpgradeToProPage. It returns only the
   * BotSpot JWT. Delete it after both callers migrate to getApiToken.
   */
  const getAccessTokenSilently = useCallback(async () => {
    console.debug('[useUser] getAccessTokenSilently called, auth method:', authMethod);

    if (!isAuthenticated || authMethod !== 'custom_jwt') {
      console.warn('[useUser] No authentication method available for token retrieval');
      return null;
    }

    return await getApiToken();
  }, [authMethod, getApiToken, isAuthenticated]);

  /**
   * COMPREHENSIVE LOGGING FOR DEBUGGING
   * Tracks authentication state changes for troubleshooting
   * Uses stable userEmail to prevent excessive logging
   */
  useEffect(() => {
    console.debug('[useUser] Authentication state summary:', {
      isAuthenticated,
      authMethod,
      userEmail,
      hasProfile: !!userProfile,
      isAdmin,
      isBetaTester,
      isPayingCustomer,
      paymentError,
      requiresSessionRefresh,
      loading: {
        auth: authLoading,
        profile: profileLoading,
        payment: paymentLoading
      }
    });
  }, [isAuthenticated, authMethod, userEmail, userProfile, isAdmin, isBetaTester, isPayingCustomer, paymentError, requiresSessionRefresh, authLoading, profileLoading, paymentLoading]);

  // An authenticated render can happen one paint before the profile-fetch
  // effect marks itself as loading. Keep profile-dependent routes pending until
  // that first profile has either loaded or fallen back explicitly.
  const isWaitingForInitialProfile = Boolean(
    isAuthenticated && (userId || userSub) && !userProfile,
  );
  const isLoading = authLoading || profileLoading || isWaitingForInitialProfile;

  // Return unified authentication interface
  return {
    // Authentication state
    isAuthenticated,
    isLoading,
    user: userProfile || user, // Prefer detailed profile over basic auth user
    
    // Authentication methods
    authMethod, // 'custom_jwt' | null
    authSessionExchangeFailed,
    
    // Authentication actions
    loginWithRedirect,
    logout,
    handleCustomJwtAuth,
    clearAuthSessionExchangeFailure,
    
    // Token management
    getAccessTokenSilently, // Temporary BotSpot JWT compatibility adapter
    getApiToken,
    getFreshLoginIdToken,
    
    // User profile data
    userProfile,
    isAdmin,
  isBetaTester,
    
    // Payment status (from separate context)
    isPayingCustomer,
    hasProSubscription: isPayingCustomer, // Alias for compatibility with AIBootcamp component
    paymentError,
    requiresSessionRefresh,
    
    // Profile management
    refreshProfile,
    
    // Loading states
    authLoading,
    profileLoading,
    paymentLoading,
    
    // Raw authentication data for debugging
    rawAuthUser: user,
    debugInfo: {
      authMethod,
      hasStoredJwt: !!safeGetStorageItem('localStorage', 'customAuthToken'),
      userAgent: navigator.userAgent.includes('Chrome') ? 'Chrome' : 'Other'
    }
  };
};

export default useUser;
